<div class="quotes">
  @switch (viewState()) {
    @case ("detail") {
      <app-quote-details
        [estimate]="selectedEstimate()!"
        [order]="order()"
        [isLowestRate]="isLowestRate(selectedEstimate()!)"
        (back)="onBack()"
        (quoteConfirmed)="quoteConfirmed.emit($event)"
      />
    }

    @case ("progress") {
      <app-quote-progress
        [order]="order()!"
        [cancelledEstimates]="cancelledEstimates()"
      />
    }

    @case ("final") {
      <app-quote-final
        [estimate]="selectedVendorEstimate()!"
        [order]="order()!"
        [cancelledEstimates]="cancelledEstimates()"
      />
    }

    @case ("rejected") {
      <section
        class="quotes__rejected-guidance"
        role="status"
        aria-live="polite"
        data-testid="quotes-rejected-guidance"
      >
        <img src="assets/icons/warning.svg" alt="Warning" />
        <p class="quotes__rejected-guidance-text">
          No vendors are currently available to support your request.<br />For
          assistance, please contact
          <a
            class="quotes__rejected-guidance-link"
            [href]="rejectedSupportHref()"
            data-testid="rejected-support-link"
          >
            support
          </a>
          .
        </p>
      </section>

      <app-vendor-rejections-accordion
        [order]="order()!"
        [rejectedEstimates]="rejectedEstimates()"
        [rejectedVendorCount]="rejectedVendorCount()"
      />
    }

    @case ("pending") {
      @for (
        estimate of pendingEstimates();
        track estimate.vendorId ?? estimate.estimationsId ?? $index
      ) {
        <app-quote-card
          [order]="order()!"
          [estimate]="estimate"
          [orderDeadline]="orderDeadline()"
          [isPending]="true"
          [readonlyMode]="true"
        />
      }

      @if (isPartialRejectionScenario()) {
        <app-vendor-rejections-accordion
          [order]="order()!"
          [rejectedEstimates]="rejectedEstimates()"
          [rejectedVendorCount]="rejectedVendorCount()"
        />
      }
    }

    @case ("cancelledPending") {
      <p
        class="quotes__info-message"
        role="status"
        data-testid="quotes-cancelled-pending-message"
      >
        Job was cancelled before any quotes were provided.
      </p>
    }

    @case ("cancelledExpired") {
      <section
        class="quotes__expired-warning"
        role="status"
        aria-live="polite"
        data-testid="quotes-expired-warning-message"
      >
        <img
          src="assets/icons/warning.svg"
          alt="Warning"
          class="quotes__expired-warning-icon"
        />
        <p class="quotes__expired-warning-text">
          All vendor quotes have expired.<br />
          If needed, please create a new job.
        </p>
      </section>

      <app-expired-quotes-accordion
        [estimates]="allEstimates()"
        [orderDeadline]="orderDeadline()"
      />
    }

    @case ("cancelledReviewed") {
      <section data-testid="quotes-readonly-container">
        @for (
          estimate of cancelledApprovedEstimates();
          track estimate.vendorId ?? estimate.estimationsId ?? $index
        ) {
          <app-quote-card
            [order]="order()!"
            [estimate]="estimate"
            [orderDeadline]="orderDeadline()"
            [isLowestStandard]="isLowestRate(estimate)"
            [readonlyMode]="false"
            (viewDetails)="onViewDetails($event)"
          />
        }

        @for (
          estimate of cancelledUnrespondedEstimates();
          track estimate.vendorId ?? estimate.estimationsId ?? $index
        ) {
          <app-quote-card
            [order]="order()!"
            [estimate]="estimate"
            [cancellationReason]="estimate.cancellationReason ?? ''"
            [orderDeadline]="orderDeadline()"
            [readonlyMode]="true"
          />
        }

        @for (
          estimate of cancelledRejectedEstimates();
          track estimate.vendorId ?? estimate.estimationsId ?? $index
        ) {
          <app-quote-card
            [order]="order()!"
            [estimate]="estimate"
            [readonlyMode]="true"
          />
        }
      </section>
    }

    @case ("reviewed") {
      @if (shouldShowInfoMessage()) {
        <p
          class="quotes__info-message"
          role="status"
          data-testid="quotes-info-message"
        >
          The delivery dates shown are calculated under the assumption that the
          purchase order is submitted today.
        </p>
      }

      @for (
        estimate of estimatesWithQuotes();
        track estimate.vendorId ?? estimate.estimationsId ?? $index
      ) {
        <app-quote-card
          [order]="order()!"
          [estimate]="estimate"
          [orderDeadline]="orderDeadline()"
          [isLowestStandard]="isLowestRate(estimate)"
          [isPending]="false"
          (viewDetails)="onViewDetails($event)"
          (quoteConfirmed)="quoteConfirmed.emit($event)"
        />
      }

      @for (
        estimate of estimatesWithoutQuotes();
        track estimate.vendorId ?? estimate.estimationsId ?? $index
      ) {
        <app-quote-card
          [order]="order()!"
          [estimate]="estimate"
          [orderDeadline]="orderDeadline()"
          [isPending]="true"
          [readonlyMode]="true"
        />
      }

      <app-cancelled-estimates-list
        [order]="order()!"
        [estimates]="cancelledStatusEstimates()"
        [orderDeadline]="orderDeadline()"
      />

      @if (isPartialRejectionScenario()) {
        <app-vendor-rejections-accordion
          [order]="order()!"
          [rejectedEstimates]="rejectedEstimates()"
          [rejectedVendorCount]="rejectedVendorCount()"
        />
      }
    }

    @case ("fallback") {
      @for (
        estimate of allEstimates();
        track estimate.vendorId ?? estimate.estimationsId ?? $index
      ) {
        <app-quote-card
          [order]="order()!"
          [estimate]="estimate"
          [orderDeadline]="orderDeadline()"
          [isLowestStandard]="isLowestRate(estimate)"
          (viewDetails)="onViewDetails($event)"
          (quoteConfirmed)="quoteConfirmed.emit($event)"
        />
      }
    }

    @case ("notAvailable") {
      <div class="quotes__not-available" role="status" aria-live="polite">
        <mat-icon class="quotes__not-available-icon" aria-hidden="true"
          >warning</mat-icon
        >
        <p class="quotes__not-available-text">
          No matching suppliers cover the selected language(s).
          <br />
          For assistance, please contact
          <a
            class="quotes__not-available-link"
            href="mailto:AZTranslate@astrazeneca.com"
            >support</a
          >.
        </p>
      </div>
    }

    @case ("empty") {
      <p class="quotes__empty">No vendors assigned yet.</p>
    }
  }
</div>
